import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { useDebounce } from "@uidotdev/usehooks";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drink } from "src/lib/db";
import { cacheDrink } from "src/lib/slices/drinkCache";
import { cn } from "src/lib/utils";
import useSWR from "swr";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const fetcher = (url) => fetch(url).then((res) => res.json());

export default function DrinkSearch({ open, setOpen, selectDrink }) {
  const dispatch = useDispatch();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(null);

  const cachedDrinks: Drink[] = useSelector(
    (state: any) => state.drinkCache.drinks
  ).map((r) => r.drink);

  useEffect(() => {
    if (searchValue === null) return;
    setSearchValue(null);
    selectDrink(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchValue, setOpen]);

  const [searchTextValue, setSearchTextValue] = useState("");

  const debouncedSearchTextValue = useDebounce(searchTextValue, 300);

  const shouldFetch = debouncedSearchTextValue.length >= 2;

  const { data, isLoading } = useSWR(
    shouldFetch
      ? `${process.env.REACT_APP_API_URL}/drinks/search/${debouncedSearchTextValue}`
      : null,
    fetcher,
    {}
  );

  const filteredDrinksSearch = useMemo(() => {
    if (!data?.drinks) return [];
    return data.drinks.filter(
      (drink) => !cachedDrinks.find((r) => r.id === drink.id)
    );
  }, [cachedDrinks, data]);

  const drinksCombined = useMemo(() => {
    if (!debouncedSearchTextValue) return cachedDrinks.slice(0, 10);

    const fuse = new Fuse(cachedDrinks, {
      keys: ["name"],
      includeScore: true,
    });

    const results = fuse.search(debouncedSearchTextValue);

    const apiResultsWithScores = filteredDrinksSearch.map((result, index) => ({
      item: result,
      score: data?.scores?.[index] ?? 0,
    }));

    const combined = [...results, ...apiResultsWithScores]
      .sort((a, b) => a.score - b.score)
      .map((r) => r.item)
      .slice(0, 10);

    return combined;
  }, [
    cachedDrinks,
    data?.scores,
    filteredDrinksSearch,
    debouncedSearchTextValue,
  ]);

  return (
    <Popover open={searchOpen} onOpenChange={setSearchOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={searchOpen}
          className="justify-between"
        >
          {"Vyhledat nápoj..."}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <Command filter={() => 1}>
          <CommandInput
            placeholder="Vyhledat nápoj..."
            className="h-9"
            value={searchTextValue}
            onValueChange={setSearchTextValue}
          />
          <CommandEmpty>
            {isLoading || debouncedSearchTextValue !== searchTextValue
              ? `Načítání...`
              : `Žádné nápoje nenalezeny.`}
          </CommandEmpty>
          <CommandGroup>
            {drinksCombined.map((drink) => (
              <Item
                drink={drink}
                setSearchValue={setSearchValue}
                setSearchOpen={setSearchOpen}
                searchValue={searchValue}
                key={drink.id}
                dispatch={
                  cachedDrinks.find((d) => d.id === drink.id) ? null : dispatch
                }
              />
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const Item = ({
  drink,
  setSearchValue,
  setSearchOpen,
  searchValue,
  dispatch,
}: {
  drink: Drink;
  setSearchValue: any;
  setSearchOpen: any;
  searchValue: any;
  dispatch?: any;
}) => {
  return (
    <CommandItem
      key={drink.id}
      value={drink.id.toString()}
      onSelect={(currentValue) => {
        if (dispatch) dispatch(cacheDrink({ drink }));
        setSearchValue(drink.id === searchValue ? null : drink.id);
        setSearchOpen(false);
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "1.25rem",
        }}
      >
        <img
          src={`${process.env.REACT_APP_API_URL}/images/drink/${drink.icon}`}
          alt=""
          style={{ height: "1.25rem", paddingRight: "1ch" }}
        />
      </div>
      <div>
        {drink.name}{" "}
        <span style={{ fontSize: "0.75rem" }}>({drink.alcoholVolume} %)</span>
      </div>
      <CheckIcon
        className={cn(
          "ml-auto h-4 w-4",
          searchValue === drink.id ? "opacity-100" : "opacity-0"
        )}
      />
    </CommandItem>
  );
};
